import { useEffect, useState, type ReactNode } from 'react';
import {
  Award, Building2, Camera, Check, Code2, Globe2, GraduationCap, IdCard, Palette, PenLine,
  Plus, Presentation, RefreshCw, Scale, ShieldCheck, ShoppingCart, Stamp, Wallet, X, Youtube,
} from 'lucide-react';
import '@/styles/landingHT.css';

/* ────────────────────────────────────────────────────────────────────────────
   Building blocks for the human-touch landings (`.ht`, see styles/landingHT.css)

     /            RunComply.tsx        — setup + accounting + legal
     /accounting  Accounting.tsx       — books, VAT, corporate tax
     /corporate   CorporateServices.tsx — setup, visas, PRO   (orange variant)

   The three reference prototypes share these sections almost verbatim, so they
   live here once. Anything genuinely page-specific (hero visuals, the pillars
   grid, pricing) stays in the page.
   ──────────────────────────────────────────────────────────────────────────── */

/** Scroll-reveal for `.rv` elements inside `.ht`. One observer for the page. */
export function HTReveal() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>('.ht .rv'));
    if (!els.length) return;
    const io = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -6% 0px', threshold: .08 });
    for (const el of els) {
      el.setAttribute('data-reveal', '');
      // Already on screen at mount → show at once, so the fold is never blank.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) el.classList.add('in');
      else io.observe(el);
    }
    return () => io.disconnect();
  }, []);
  return null;
}

/** The tick inside a feature-list bullet. The caller owns the surrounding chip. */
export const Ck = ({ size = 13 }: { size?: number }) => <Check size={size} strokeWidth={3.4} />;

/** Centred section header: kicker, heading, optional lead. */
export function SHead({ k, h2, lead, id }: { k: string; h2: ReactNode; lead?: ReactNode; id?: string }) {
  return (
    <div className="shead rv" id={id}>
      <div className="k">{k}</div>
      <h2>{h2}</h2>
      {lead && <p>{lead}</p>}
    </div>
  );
}

/* ── audience grid ────────────────────────────────────────────────────────── */
export type Audience = { icon: ReactNode; bg: string; fg: string; title: string; sub: string };

export function AudienceGrid({ items }: { items: Audience[] }) {
  return (
    <div className="aud">
      {items.map((a, i) => (
        <div className="acard rv" style={{ transitionDelay: `${(i % 4) * 60}ms` }} key={a.title}>
          <span className="ic" style={{ background: a.bg, color: a.fg }}>{a.icon}</span>
          <h3>{a.title}</h3>
          <p>{a.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* Digital-professional audiences — the accounting & home pages.
   "OnlyFans" from the reference deck is deliberately not here: adult platforms are
   illegal to operate from the UAE, so naming one on a UAE compliance page would be
   advertising work we can't file. Patreon carries the same "paid audience" meaning. */
export const ONLINE_AUDIENCE: Audience[] = [
  { icon: <Youtube size={21} />, bg: '#FDE7E4', fg: '#F0776B', title: 'Content creators', sub: 'YouTube · Twitch · TikTok · Patreon' },
  { icon: <Code2 size={21} />, bg: '#E7EEFB', fg: '#5B8DEF', title: 'Developers', sub: 'Freelance · US clients · SaaS' },
  { icon: <Palette size={21} />, bg: '#FCE4EF', fg: '#E85C9A', title: 'Designers', sub: 'Illustration · Branding · Etsy' },
  { icon: <GraduationCap size={21} />, bg: '#FDF0DA', fg: '#F0A640', title: 'Online educators', sub: 'Hotmart · Udemy · own academies' },
  { icon: <Presentation size={21} />, bg: '#EEE7FA', fg: '#7A4FC0', title: 'Consultants', sub: 'Marketing · Coaching · B2B' },
  { icon: <ShoppingCart size={21} />, bg: '#E4F3EA', fg: '#15824E', title: 'E-commerce', sub: 'Shopify · Amazon · Dropshipping' },
  { icon: <Camera size={21} />, bg: '#EAE7FB', fg: '#6C5CE7', title: 'Photo & video', sub: 'Stock · Weddings · Licensing' },
  { icon: <PenLine size={21} />, bg: '#E4F3EA', fg: '#15824E', title: 'Copywriters', sub: 'Freelance · Substack · Newsletters' },
];

/** Corporate Services — what the one-team desk actually covers. */
export const CORPORATE_AUDIENCE: Audience[] = [
  { icon: <Building2 size={21} />, bg: '#FCE4D6', fg: '#D9662E', title: 'Mainland setup', sub: 'Dubai DED · trade licence' },
  { icon: <Globe2 size={21} />, bg: '#E7EEFB', fg: '#2E6BD6', title: 'Free zone setup', sub: 'Meydan · IFZA · DMCC · 40+ zones' },
  { icon: <IdCard size={21} />, bg: '#E4F3EA', fg: '#15824E', title: 'Residence visas', sub: 'Founder · employee · family' },
  { icon: <Award size={21} />, bg: '#FDF0DA', fg: '#F0A640', title: 'Golden Visa', sub: '10-year residence · investors & talent' },
  { icon: <Stamp size={21} />, bg: '#EEE7FA', fg: '#7A4FC0', title: 'PRO & renewals', sub: 'Amendments · NOCs · attestation' },
  { icon: <Wallet size={21} />, bg: '#E1F4F1', fg: '#0E9E8E', title: 'WPS payroll', sub: 'Wage protection, run for you' },
  { icon: <Scale size={21} />, bg: '#FCE4EF', fg: '#E85C9A', title: 'Wills & POA', sub: 'DIFC wills · powers of attorney' },
  { icon: <RefreshCw size={21} />, bg: '#FCE4D6', fg: '#D9662E', title: 'Licence renewals', sub: 'Nothing lapses — a managed calendar' },
];

/* ── how we work: app mock beside numbered steps ──────────────────────────── */
export type Step = { h: string; p: string };

export function StepList({ steps }: { steps: Step[] }) {
  return (
    <div className="steps">
      {steps.map((s, i) => (
        <div className="step rv" style={{ transitionDelay: `${i * 80}ms` }} key={s.h}>
          <div className="n">{i + 1}</div>
          <div><h3>{s.h}</h3><p>{s.p}</p></div>
        </div>
      ))}
    </div>
  );
}

/** The browser-chrome frame the "how we work" mock sits in. */
export function AppFrame({ nav, active, children }: { nav: string[]; active?: string; children: ReactNode }) {
  return (
    <div className="dash rv">
      <div className="bar">
        <i style={{ background: '#E15D5D' }} /><i style={{ background: '#F0B429' }} /><i style={{ background: '#3CC26E' }} />
      </div>
      <div className="dbody">
        <div className="dnav">
          {nav.map(n => <div className={`di${n === (active ?? nav[0]) ? ' on' : ''}`} key={n}>{n}</div>)}
        </div>
        <div className="dmain">{children}</div>
      </div>
    </div>
  );
}

/* ── human touch band ─────────────────────────────────────────────────────── */
export type ChatLine = { from: 'them' | 'you'; text: string };

export function HumanBand({
  title, lead, feats, adviser, role, avatar, chat,
}: {
  title: ReactNode; lead: string; feats: string[];
  adviser: string; role: string; avatar: ReactNode; chat: ChatLine[];
}) {
  return (
    <div className="human rv">
      <div>
        <span className="eyebrow soft">The human touch</span>
        <h2>{title}</h2>
        <p>{lead}</p>
        <div className="feats">{feats.map(f => <span key={f}>{f}</span>)}</div>
      </div>
      <div className="adv">
        <div className="advhead">
          <span className="av">{avatar}</span>
          <div><b>{adviser} · {role}</b><span className="dotline" style={{ color: '#3CD68A' }}>Online now</span></div>
        </div>
        <div className="chat">
          {chat.map((c, i) => <div className={`bub ${c.from}`} key={i}>{c.text}</div>)}
        </div>
      </div>
    </div>
  );
}

/* ── proof: stat row + one client story ───────────────────────────────────── */
export function Stats({ items }: { items: { b: string; s: ReactNode }[] }) {
  return (
    <div className="stats">
      {items.map((s, i) => (
        <div className="stat rv" style={{ transitionDelay: `${i * 60}ms` }} key={s.b + i}>
          <b>{s.b}</b><span>{s.s}</span>
        </div>
      ))}
    </div>
  );
}

export function ClientStory({
  quote, name, note, initial, photo, caption,
}: { quote: string; name: string; note: string; initial: string; photo: string; caption: string }) {
  return (
    <div className="testi rv">
      <div>
        <div className="k">Client story</div>
        <div className="q">“{quote}”</div>
        <div className="who">
          <div className="pl">{initial}</div>
          <div>
            <b style={{ fontFamily: 'Poppins', fontSize: 14 }}>{name}</b>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{note}</div>
          </div>
        </div>
      </div>
      {/* A still, not a video: there is no client film to play, and a play button
          with nothing behind it is worse than none. */}
      <figure className="shot">
        <img src={photo} alt="" loading="lazy" />
        <figcaption>{caption}</figcaption>
      </figure>
    </div>
  );
}

/* ── comparison table ─────────────────────────────────────────────────────── */
export type CmpRow = [string, string, string, string];

export function Compare({ cols, rows }: { cols: [string, string, string]; rows: CmpRow[] }) {
  const cell = (v: string) =>
    v === 'yes' ? <Check size={17} strokeWidth={3} className="y" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
    : v === 'no' ? <X size={17} strokeWidth={3} className="n" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
    : <span className="p">{v}</span>;
  return (
    <div className="cmpwrap rv">
      <div className="cmpscroll">
        <table className="compare">
          <thead>
            <tr>
              <th className="cap" aria-label="Capability" />
              <th className="us">{cols[0]}</th><th>{cols[1]}</th><th>{cols[2]}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([cap, a, b, c]) => (
              <tr key={cap}>
                <td className="cap">{cap}</td>
                <td className="usc">{cell(a)}</td>
                <td>{cell(b)}</td>
                <td>{cell(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── FAQ (accordion, one open at a time) ──────────────────────────────────── */
export function Faq({ items }: { items: { q: string; a: ReactNode }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="faq">
      {items.map((f, i) => (
        <div className={`qa rv${open === i ? ' open' : ''}`} key={f.q}>
          <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
            {f.q}<Plus className="pm" size={20} strokeWidth={2.2} aria-hidden />
          </button>
          <div className="a"><p>{f.a}</p></div>
        </div>
      ))}
    </div>
  );
}

/* ── guarantee strip ──────────────────────────────────────────────────────── */
export function Guarantee({ title, body }: { title: string; body: string }) {
  return (
    <div className="guarantee rv">
      <span className="g-ic"><ShieldCheck size={24} strokeWidth={2} /></span>
      <div><b>{title}</b><p>{body}</p></div>
    </div>
  );
}

/* ── final CTA ────────────────────────────────────────────────────────────── */
export function CtaBand({ title, body, action }: { title: string; body: string; action: ReactNode }) {
  return (
    <section className="sec">
      <div className="wrap">
        <div className="cta rv">
          <h2>{title}</h2>
          <p>{body}</p>
          {action}
        </div>
      </div>
    </section>
  );
}
